import { Link, useNavigate } from "react-router-dom";

import {
  ArrowRight,
  CreditCard,
  FileText,
  Store,
  Shield,
  Zap,
  Globe,
  CheckCircle2,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Logo } from "../components/Logo";
import { LanguageToggle } from "../components/LanguageToggle";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import { useEffect } from "react";
import { getPostLoginRoute } from "../lib/authRoutes";

const Index = () => {
  const { isAuth, user } = useAuth()
  const { t } = useTranslation();
  const navigate = useNavigate();

  const features = [
    {
      icon: FileText,
      title: t("createInvoicesTitle"),
      description: t("createInvoicesDesc"),
    },
    {
      icon: CreditCard,
      title: t("acceptPaymentsTitle"),
      description: t("acceptPaymentsDesc"),
    },
    {
      icon: Store,
      title: t("merchantToolsTitle"),
      description: t("merchantToolsDesc"),
    },
    {
      icon: Shield,
      title: t("securityTitle"),
      description: t("securityDesc"),
    },
  ];

  const benefits = [
    t("feesCost"),
    t("paymentNotif"),
    t("multiCurrency"),
    t("mobileFriendly"),
  ];

  useEffect(() => {
    if (isAuth()) {
      navigate(getPostLoginRoute(user))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Logo />
            <div className="flex items-center gap-4">
              <LanguageToggle />
              <Button variant="ghost" asChild>
                <Link to="/login">{t("login")}</Link>
              </Button>
              <Button variant="gradient" asChild>
                <Link to="/register">
                  {t("register")}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mx-auto text-center animate-fade-up">
            <div className="inline-flex items-center gap-2 bg-accent px-4 py-2 rounded-full text-sm font-medium text-accent-foreground mb-8">
              <Zap className="h-4 w-4" />
              {t("securePayments")}
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-foreground leading-tight mb-6">
              {t("acceptPayments")}
              <br />
              <span className="text-primary">{t("withoutHassle")}</span>
            </h1>

            <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
              {t("descMsg")}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button variant="gradient" size="xl" asChild>
                <Link to="/register">
                  {t("getStartedFree")}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button variant="outline" size="xl" asChild>
                <Link to="/login">
                  <Globe className="mr-2 h-5 w-5" />
                  {t("login")}
                </Link>
              </Button>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap items-center justify-center gap-8 mt-12 text-muted-foreground">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-success" />
                  {benefit}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              {t("headline")}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t("subheadline")}
            </p>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((feature, index) => (
              <div
                key={index}
                className="card-interactive p-6 animate-fade-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="card-elevated p-8 sm:p-12 text-center bg-gradient-to-br from-primary/5 to-primary/10">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              {t("readyTitle")}
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto">
              {t("readyDesc")}
            </p>
            <Button variant="gradient" size="xl" asChild>
              <Link to="/register">
                {t("createAccount")}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-4 px-4 sm:px-6 lg:px-8 border-t border-border mt-auto">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
            <Logo size="md" />
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} PayLink Negdi. {t("securePayments")}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
